import React, { useEffect } from "react";
import { FONTS, FONT_NAMES, ARABIC_FONTS, fontStack, fontWeight } from "@hudhud/reel";
import { isRtl, type CaptionLang } from "@hudhud/core";

/** A line in each language, so a font is judged on the script it will actually set. */
export const SAMPLE: Record<string, string> = {
  en: "Indeed, Allah is with the patient", ar: "بسم الله الرحمن الرحيم", ur: "سب سے زیادہ ڈر شرکِ اصغر کا", tr: "Şüphesiz Allah sabredenlerle", fr: "Certes, Allah est avec les patients",
  id: "Sesungguhnya Allah bersama orang sabar", so: "Hubaal Alle wuxuu la jiraa samirka", bs: "Zaista, Allah je sa strpljivima",
};

const loaded = new Set<string>();
/** Loads the studio fonts into the page once, so the samples show the real letterforms. */
export function useLoadedFonts(families: readonly string[]) {
  useEffect(() => {
    for (const family of families) {
      const def = FONTS[family];
      if (!def || loaded.has(family)) continue;
      loaded.add(family);
      for (const face of def.faces) {
        try { new FontFace(family, `url(/assets/fonts/${face.file})`, { weight: face.weight }).load().then((f) => (document.fonts as unknown as { add: (f: FontFace) => void }).add(f)).catch(() => undefined); } catch { /* ignore */ }
      }
    }
  }, [families]);
}

/** Pick a font for one language: every option shown in that language's script. `value` null means the studio default (`fallback`). */
export const LangFontPick: React.FC<{ lang: CaptionLang; value: string | null; fallback: string; onChange: (font: string | null) => void; allowDefault?: boolean }> = ({ lang, value, fallback, onChange, allowDefault = true }) => {
  const rtl = isRtl(lang);
  const options = rtl ? ARABIC_FONTS : FONT_NAMES;
  useLoadedFonts(options);
  const tile = (font: string, on: boolean, label: string, pick: () => void, title: string) => (
    <button key={label} className={`pick${on ? " on" : ""}`} onClick={pick} title={title} style={{ padding: "10px 8px", minHeight: 74, gap: 6 }}>
      <span style={{ fontFamily: fontStack(font), fontWeight: fontWeight(font, 800), fontSize: rtl ? 22 : 17, direction: rtl ? "rtl" : "ltr", lineHeight: 1.45, color: "var(--text)", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{SAMPLE[lang] ?? SAMPLE.en}</span>
      <span className="hint" style={{ fontSize: 10 }}>{label}</span>
    </button>
  );
  return (
    <div className="pick-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}>
      {allowDefault ? tile(fallback, value == null, `Default · ${fallback}`, () => onChange(null), "Use the studio's default for this language (set under Settings)") : null}
      {options.map((f) => tile(f, value === f, f, () => onChange(f), f))}
    </div>
  );
};
